/* Product reviews — client-side hydration.
   Fetches Judge.me's storefront-safe widget endpoint and parses the
   pre-rendered HTML fragment in `response.widget` into our own
   card-template clones. See sections/product-reviews.liquid for the
   shell + template + data-* contract. */

(function () {
  'use strict';

  const ENDPOINT = 'https://judge.me/api/v1/widgets/product_review';
  const MAX_PAGES = 200;
  const INIT_FLAG = 'pdpReviewsInit';

  function init(root) {
    if (!root || root.dataset[INIT_FLAG] === '1') return;
    root.dataset[INIT_FLAG] = '1';

    const token = root.dataset.pdpReviewsToken;
    const shop = root.dataset.pdpReviewsShop;
    const productId = root.dataset.pdpReviewsProductId;
    const perPage = parseInt(root.dataset.pdpReviewsPerPage, 10) || 6;
    const ssrCount = parseInt(root.dataset.pdpReviewsSsrCount, 10) || 0;
    // Localised star label, e.g. "__RATING__ von 5 Sternen". Rendered by the
    // section because Liquid does not run in asset files. Falls back to the
    // English shape only if the attribute is missing entirely.
    const starLabelTpl = root.dataset.pdpReviewsStarLabel || '__RATING__ out of 5';

    if (!token || !shop || !productId) return;

    const cardsEl = root.querySelector('[data-pdp-reviews-cards]');
    const emptyEl = root.querySelector('[data-pdp-reviews-empty]');
    const paginationEl = root.querySelector('[data-pdp-reviews-pagination]');
    const tmpl = root.querySelector('[data-pdp-reviews-card-template]');
    if (!cardsEl || !tmpl) return;

    const prevBtn = root.querySelector('[data-pdp-reviews-prev]');
    const nextBtn = root.querySelector('[data-pdp-reviews-next]');
    const pageEl = root.querySelector('[data-pdp-reviews-page]');
    const totalPagesEl = root.querySelector('[data-pdp-reviews-total-pages]');

    let currentPage = 1;
    let totalPages = 1;

    // ---- fetch / parse ----

    function fetchWidget(page) {
      const url = `${ENDPOINT}?api_token=${encodeURIComponent(token)}`
        + `&shop_domain=${encodeURIComponent(shop)}`
        + `&external_id=${encodeURIComponent(productId)}`
        + `&page=${page}`
        + `&per_page=${perPage}`;
      return fetch(url, { credentials: 'omit', mode: 'cors' })
        .then((r) => {
          if (!r.ok) throw new Error(`Judge.me widget endpoint returned ${r.status}`);
          return r.json();
        })
        .then((json) => parseWidget(json && json.widget ? json.widget : ''));
    }

    function parseWidget(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const widg = doc.querySelector('.jdgm-rev-widg');

      const avg = widg ? parseFloat(widg.getAttribute('data-average-rating')) : NaN;
      const total = widg ? parseInt(widg.getAttribute('data-number-of-reviews'), 10) : 0;

      const buckets = {};
      doc.querySelectorAll('.jdgm-histogram__row[data-rating]').forEach((row) => {
        const stars = parseInt(row.getAttribute('data-rating'), 10);
        const countEl = row.querySelector('.jdgm-histogram__frequency');
        const count = countEl ? parseInt(countEl.textContent.replace(/[^\d]/g, ''), 10) : 0;
        if (stars >= 1 && stars <= 5) buckets[stars] = count || 0;
      });

      const reviews = [];
      doc.querySelectorAll('.jdgm-rev-widg__reviews .jdgm-rev').forEach((el) => {
        reviews.push(parseInlineReview(el));
      });

      return {
        avg: isNaN(avg) ? null : avg,
        total: isNaN(total) ? 0 : total,
        buckets,
        reviews,
      };
    }

    function parseInlineReview(el) {
      const ratingEl = el.querySelector('.jdgm-rev__rating');
      const rating = ratingEl ? parseInt(ratingEl.getAttribute('data-score'), 10) : 0;

      const timestampEl = el.querySelector('.jdgm-rev__timestamp')
        || el.querySelector('[data-content*="-"][data-content*=":"]');
      const dateRaw = timestampEl ? timestampEl.getAttribute('data-content') : '';
      const date = formatDate(dateRaw) || (timestampEl ? timestampEl.textContent.trim() : '');

      const titleEl = el.querySelector('.jdgm-rev__title');
      const bodyEl = el.querySelector('.jdgm-rev__body');
      const authorEl = el.querySelector('.jdgm-rev__author');
      const locationEl = el.querySelector('.jdgm-rev__location');

      const photoImg = el.querySelector('.jdgm-rev__pic-img, .jdgm-rev__pic img, .jdgm-rev__pic-graphic img');
      const photo = photoImg ? (photoImg.getAttribute('src') || photoImg.getAttribute('data-src')) : '';

      const verified = el.getAttribute('data-verified-buyer') === 'true';

      return {
        id: el.getAttribute('data-review-id') || '',
        rating: clamp(rating, 0, 5),
        title: text(titleEl),
        body: text(bodyEl),
        author: text(authorEl),
        location: text(locationEl),
        country: countryFlag(text(locationEl)),
        date,
        photo,
        verified,
      };
    }

    // ---- render ----

    function renderCards(reviews) {
      cardsEl.innerHTML = '';
      const frag = document.createDocumentFragment();
      reviews.forEach((r) => {
        const card = renderCard(r);
        if (card) frag.appendChild(card);
      });
      cardsEl.appendChild(frag);
    }

    function renderCard(r) {
      const node = tmpl.content ? tmpl.content.firstElementChild.cloneNode(true) : null;
      if (!node) return null;

      const starsEl = node.querySelector('[data-card-stars]');
      if (starsEl) {
        starsEl.innerHTML = '';
        starsEl.setAttribute('aria-label', starLabelTpl.replace('__RATING__', Math.round(r.rating * 10) / 10));
        for (let i = 1; i <= 5; i++) {
          const fillPct = Math.max(0, Math.min(1, r.rating - (i - 1))) * 100;
          starsEl.insertAdjacentHTML('beforeend', starSVG(fillPct));
        }
      }

      setField(node, '[data-card-title]', r.title);
      setField(node, '[data-card-body]', r.body);
      setField(node, '[data-card-author]', r.author);
      setField(node, '[data-card-when]', r.date);
      setField(node, '[data-card-country]', r.country);

      const verifiedEl = node.querySelector('[data-card-verified]');
      if (verifiedEl) verifiedEl.hidden = !r.verified;

      const photoLink = node.querySelector('[data-card-photo-link]');
      const photoImg = node.querySelector('[data-card-photo-img]');
      if (r.photo && photoLink && photoImg) {
        photoImg.src = r.photo;
        photoImg.alt = r.title || `Review by ${r.author}`;
        photoLink.href = r.photo;
        photoLink.hidden = false;
      }

      return node;
    }

    function setField(scope, selector, value) {
      const el = scope.querySelector(selector);
      if (!el) return;
      if (value) {
        el.textContent = value;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    }

    function renderSummary(parsed) {
      if (typeof parsed.avg === 'number' && !isNaN(parsed.avg)) {
        const avgEl = root.querySelector('[data-pdp-reviews-avg]');
        if (avgEl) avgEl.textContent = parsed.avg.toFixed(1);
      }
      const countEl = root.querySelector('[data-pdp-reviews-count]');
      if (countEl && parsed.total) countEl.textContent = String(parsed.total);

      const totalBuckets = Object.values(parsed.buckets).reduce((s, n) => s + n, 0)
        || parsed.total
        || 0;

      [1, 2, 3, 4, 5].forEach((stars) => {
        const row = root.querySelector(`[data-pdp-reviews-bar="${stars}"]`);
        if (!row) return;
        const count = parsed.buckets[stars] || 0;
        const fillEl = row.querySelector('[data-pdp-reviews-bar-fill]');
        const countEl = row.querySelector('[data-pdp-reviews-bar-count]');
        const pct = totalBuckets > 0 ? (count / totalBuckets) * 100 : 0;
        if (fillEl) fillEl.style.width = `${pct}%`;
        if (countEl) countEl.textContent = String(count);
      });
    }

    function showEmpty() {
      cardsEl.hidden = true;
      if (paginationEl) paginationEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
    }

    function showCards() {
      cardsEl.hidden = false;
      if (emptyEl) emptyEl.hidden = true;
    }

    // ---- pagination ----

    function applyPagination(total) {
      totalPages = Math.min(MAX_PAGES, Math.max(1, Math.ceil(total / perPage)));
      if (pageEl) pageEl.textContent = String(currentPage);
      if (totalPagesEl) totalPagesEl.textContent = String(totalPages);

      if (paginationEl) paginationEl.hidden = totalPages <= 1;
      if (prevBtn) prevBtn.disabled = currentPage <= 1;
      if (nextBtn) nextBtn.disabled = currentPage >= totalPages;
    }

    function goToPage(target) {
      const clamped = Math.max(1, Math.min(totalPages, target));
      if (clamped === currentPage) return;

      // Scroll-anchor: snapshot the pagination's viewport-y before
      // the swap, then after the new (potentially shorter) page is
      // in the DOM, scroll the page back by the delta so the cursor
      // stays on the same control instead of jumping up.
      const anchor = paginationEl;
      const before = anchor ? anchor.getBoundingClientRect().top : 0;

      currentPage = clamped;
      loadPage().then(() => {
        if (!anchor) return;
        const after = anchor.getBoundingClientRect().top;
        window.scrollBy({ top: after - before, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
      });
    }

    function loadPage() {
      cardsEl.setAttribute('aria-busy', 'true');
      return fetchWidget(currentPage)
        .then((parsed) => {
          renderSummary(parsed);
          if (parsed.total === 0 && parsed.reviews.length === 0) {
            showEmpty();
            applyPagination(0);
            return;
          }
          showCards();
          renderCards(parsed.reviews);
          applyPagination(parsed.total);
        })
        .catch((err) => {
          console.error('[pdp-reviews]', err);
          showEmpty();
          applyPagination(0);
        })
        .finally(() => {
          cardsEl.removeAttribute('aria-busy');
        });
    }

    if (prevBtn) prevBtn.addEventListener('click', () => goToPage(currentPage - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goToPage(currentPage + 1));

    // First load.
    loadPage();

    // If the SSR aggregate said zero reviews, we can short-circuit to
    // the empty state without waiting for the network — but the fetch
    // is still useful in case the metafield is stale, so we don't
    // bail out, just hide the cards skeleton early.
    if (ssrCount === 0) {
      cardsEl.innerHTML = '';
    }
  }

  // ---- helpers ----

  function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n));
  }

  function text(el) {
    return el ? el.textContent.trim() : '';
  }

  function starSVG(fillPct) {
    return `<span class="capsule__star">`
      + `<svg viewBox="0 0 24 24" class="capsule__star-bg" aria-hidden="true"><path d="m12 2 3 6.5 7 .9-5 4.9 1.3 7L12 17.7 5.7 21.3 7 14.3 2 9.4l7-.9L12 2z"/></svg>`
      + `<span class="capsule__star-fill" style="width: ${fillPct}%"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2 3 6.5 7 .9-5 4.9 1.3 7L12 17.7 5.7 21.3 7 14.3 2 9.4l7-.9L12 2z"/></svg></span>`
      + `</span>`;
  }

  function formatDate(raw) {
    // Judge.me serves timestamps as "YYYY-MM-DD HH:MM:SS UTC" in
    // [data-content]; render them as "DD Mon YYYY" for the card foot.
    if (!raw) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (!m) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[parseInt(m[2], 10) - 1] || '';
    return `${m[3]} ${month} ${m[1]}`;
  }

  function countryFlag(location) {
    // Heuristic: pull a country name out of `location` and map to a flag emoji.
    // Judge.me's location field is freeform; this is best-effort.
    if (!location) return '';
    const map = {
      'germany': '🇩🇪', 'deutschland': '🇩🇪', 'de': '🇩🇪',
      'united states': '🇺🇸', 'usa': '🇺🇸', 'us': '🇺🇸',
      'united kingdom': '🇬🇧', 'uk': '🇬🇧', 'great britain': '🇬🇧',
      'canada': '🇨🇦', 'ca': '🇨🇦',
      'australia': '🇦🇺', 'au': '🇦🇺',
      'france': '🇫🇷', 'fr': '🇫🇷',
      'spain': '🇪🇸', 'es': '🇪🇸',
      'italy': '🇮🇹', 'it': '🇮🇹',
      'netherlands': '🇳🇱', 'nl': '🇳🇱',
      'switzerland': '🇨🇭', 'ch': '🇨🇭',
      'austria': '🇦🇹', 'at': '🇦🇹',
      'sweden': '🇸🇪', 'se': '🇸🇪',
      'norway': '🇳🇴', 'no': '🇳🇴',
      'denmark': '🇩🇰', 'dk': '🇩🇰',
      'czech republic': '🇨🇿', 'cz': '🇨🇿', 'czechia': '🇨🇿',
      'poland': '🇵🇱', 'pl': '🇵🇱',
    };
    const key = location.toLowerCase().split(',').pop().trim();
    return map[key] || '';
  }

  // ---- boot ----

  // Lootroom: the Judge.me request waits until the block is ~400px from the viewport (audit PERF-8/JS-10).
  // The reviews sit far below the fold, so the fetch no longer competes with the hero image on load.
  function bootAll() {
    const roots = document.querySelectorAll('[data-pdp-reviews]');
    if (!('IntersectionObserver' in window)) {
      roots.forEach(init);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        init(entry.target);
      });
    }, { rootMargin: '400px 0px' });
    roots.forEach((root) => io.observe(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Re-init on theme-editor section reloads.
  document.addEventListener('shopify:section:load', (e) => {
    if (!e || !e.target) return;
    e.target.querySelectorAll('[data-pdp-reviews]').forEach(init);
  });
})();
